import {t} from "i18next";
import {STATUS_MAP} from "models/options.js";
import {ACTION, FEATURE, publish} from "./index.js";

// Incidents Page Telemetry

export const sendIncidentsThemeTelemetry = (value) => {
  const payload = {
    service: FEATURE.INCIDENTS.BUTTON_THEME,
    description: "Incident List: Theme switcher / button group",
    metadata: {
      action: ACTION.SET,
      value
    }
  };
  publish(payload);
};

export const sendIncidentsDateRangeTelemetry = (value) => {
  const payload = {
    service: FEATURE.INCIDENTS.MENU_DATE_RANGE,
    description: "Incident List: Date Range dropdown menu",
    metadata: {
      action: ACTION.SET,
      value
    }
  };
  publish(payload);
};

export const sendIncidentsListTitleTelemetry = (
  incident_id,
  severity,
  short_description,
  source,
  status,
  title
) => {
  const payload = {
    service: FEATURE.INCIDENTS.BUTTON_TITLE_TEXT,
    description: "Incident List: Incident Title text link",
    metadata: {
      action: ACTION.CLICK,
      incident_id,
      severity,
      short_description,
      source,
      status,
      title
    }
  };
  publish(payload);
};

export const sendIncidentStatusTelemetry = (status, useMount, isBulkAction) => {
  // using useMount as a proxy for page origin (Incident List || Incident Details)
  const telemetryService = useMount
    ? isBulkAction
      ? FEATURE.INCIDENTS.MENU_STATUS_BULK_ACTION
      : FEATURE.INCIDENTS.MENU_STATUS
    : FEATURE.INCIDENT_DETAIL.MENU_STATUS;

  const telemetryDescription = useMount
    ? isBulkAction
      ? "Incident List: Change Status bulk action dropdown menu"
      : "Incident List: Status column dropdown menu"
    : "Incident Details: Status dropdown menu";

  const payload = {
    service: telemetryService,
    description: telemetryDescription,
    metadata: {
      action: ACTION.SET,
      ["new-status"]: status
    }
  };
  publish(payload);
};

// Incidents Table Drawer Telemetry
export const sendViewIncidentDetailsButtonTelemetry = (
  incident_id,
  severity,
  short_description,
  source,
  status,
  title
) => {
  const payload = {
    service: FEATURE.INCIDENTS.BUTTON_VIEW_INCIDENT_DETAILS,
    description: "Incident List Drawer: View Incident Details button",
    metadata: {
      action: ACTION.CLICK,
      incident_id,
      severity,
      short_description,
      source,
      status,
      title
    }
  };
  publish(payload);
};

// Incident Details Page Telemetrty

export const sendInvestigateIncidentButtonTelemetry = () => {
  const payload = {
    service: FEATURE.INCIDENT_DETAIL.BUTTON_INVESTIGATE_INCIDENT,
    description: "Incident Details: Investigate Incident button",
    metadata: {
      action: ACTION.CLICK
      // add incident: incidentId when available
    }
  };
  publish(payload);
};

export const sendIncidentsViewLongDescriptionTelemetry = (incidentId) => {
  const payload = {
    service: FEATURE.INCIDENT_DETAIL.LONG_DESCRIPTION,
    description: "Incident Details: View Long Description",
    metadata: {
      action: ACTION.CLICK,
      buttonName: t("incidentDetail.detail.longDescriptionBtn"),
      incident: incidentId
    }
  };
  publish(payload);
};

export const sendIncidentsLongDescriptionTelemetry = (
  incidentId,
  buttonName
) => {
  const action =
    buttonName === t("incidentDetail.detail.description.editMarkdown")
      ? ACTION.EDIT
      : buttonName === t("incidentDetail.detail.description.saveMarkdown")
      ? ACTION.SAVE
      : ACTION.CANCEL;

  const payload = {
    service: FEATURE.INCIDENT_DETAIL.LONG_DESCRIPTION,
    description: "Incident Details: Long Description edit / save / cancel",
    metadata: {
      action,
      buttonName,
      incident: incidentId
    }
  };
  publish(payload);
};

// Incident Details Tabs: Overview, Detection, Response, Worklog
export const sendIncidentDetailsTabsTelemetry = (tab) => {
  const titleCasedTab = `${tab.charAt(0).toUpperCase()}${tab
    .substr(1)
    .toLowerCase()}`;
  const tabNameObjValue = `TAB_${tab}`;
  const payload = {
    service: FEATURE.INCIDENT_DETAIL[tabNameObjValue],
    description: `Incident Details: ${titleCasedTab} tab`,
    metadata: {
      action: ACTION.CLICK
    }
  };
  publish(payload);
};

// Incident Overview Panal View Links (View Assets, View Observables, View Indicators)
export const sendIncidentOverviewPanelViewLinksTelemetry = (
  panelName,
  count
) => {
  const panelNameObjValue = `LINK_VIEW_${panelName.toUpperCase()}`;

  const payload = {
    service: FEATURE.INCIDENT_DETAIL[panelNameObjValue],
    description: `Incident Overview: ${panelName} panel link`,
    metadata: {
      action: ACTION.CLICK,
      [`${panelName}-count`]: count
    }
  };
  publish(payload);
};

// Incident Detail Response Tab
export const sendIncidentDetailsResponseAddNoteTelemetry = (
  incidentId,
  stage,
  task
) => {
  const payload = {
    service: FEATURE.INCIDENT_DETAIL.RESPONSE_ADD_NOTE,
    description: "Incident Details: Response Action Add Note",
    metadata: {
      action: ACTION.CLICK,
      buttonName: t("notes.addNote"),
      incident: incidentId,
      stage,
      task
    }
  };
  publish(payload);
};

export const sendIncidentDetailsResponseSaveNoteTelemetry = (
  incidentId,
  buttonName,
  stage,
  task
) => {
  const action = buttonName === t("misc.save") ? ACTION.SAVE : ACTION.CANCEL;

  const payload = {
    service: FEATURE.INCIDENT_DETAIL.RESPONSE_ADD_NOTE,
    description: "Incident Details: Response Action Note Save / Cancel",
    metadata: {
      action,
      buttonName,
      incident: incidentId,
      stage,
      task
    }
  };
  publish(payload);
};

export const sendIncidentResponseCloseIncidentTelemetry = (incidentId) => {
  const payload = {
    service: FEATURE.INCIDENT_DETAIL.RESPONSE_CLOSE_INCIDENT,
    description: "Incident Details: Response - Close incident from last step ",
    metadata: {
      action: ACTION.SET,
      ["new-status"]: STATUS_MAP.Closed.value,
      incident: incidentId
    }
  };
  publish(payload);
};

// Incident Details: Worklog Tab Telemetry
export const sendNotesAuditLogToggleTelemetry = (
  panelName,
  incident_id,
  notesCount,
  auditLogCount
) => {
  const panelNameObjValue = `WORKLOG_BUTTON_${panelName
    .replace(/\s/g, "") // remove whitespace
    .toUpperCase()}`;

  const panelCountMetadataKey = `${panelName
    .replace(/\s/g, "-")
    .toLowerCase()}-count`;

  const telemetricCount =
    panelName === t("incidentDetail.worklogTab.notesTab")
      ? notesCount
      : auditLogCount;

  const payload = {
    service: FEATURE.INCIDENT_DETAIL[panelNameObjValue],
    description: `Incident Detail > Worklog Tab > ${panelName} panel link`,
    metadata: {
      action: ACTION.CLICK,
      incident_id,
      [panelCountMetadataKey]: telemetricCount
    }
  };
  publish(payload);
};

// @todo: rename this to be Save Note when implementing telemetry for the Add Note button
export const sendIncidentDetailsAddNoteTelemetry = (incidentId, buttonName) => {
  const action =
    buttonName === t("incidentDetail.detail.description.saveMarkdown")
      ? ACTION.SAVE
      : ACTION.CANCEL;

  const payload = {
    service: FEATURE.INCIDENT_DETAIL.WORKLOG_ADD_NOTE,
    description: "Incident Details: Work Log Add a Note Save / Cancel",
    metadata: {
      action,
      buttonName,
      incident: incidentId
    }
  };
  publish(payload);
};
